import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';

/**
 * SubAccountPicker — Discover & pick a sub-account on Tradovate / Volumetrica
 * connections. Many prop firms (APEX, AquaFutures) issue multiple eval/funded
 * accounts under a single broker login (e.g. APEX_375223, APEX_375224 or
 * AQ13288, AQ13289). This component:
 *   1. Lets the user trigger a Playwright sub-account scrape on demand.
 *   2. Polls the connection list every 4s until `discovery_status` flips to ok.
 *   3. Renders the discovered list as a tappable radio-button picker.
 *   4. Persists the selection via /select-sub-account.
 *
 * Props:
 *   platform: 'tradovate' | 'volumetrica'
 *   connection: the connection doc (with .connection_id, .sub_accounts,
 *               .discovery_status, .selected_sub_account_id or .account_number)
 *   onUpdate:   () => void — called after discovery completes or pick succeeds
 *               so the parent can refresh its connection list.
 */
export default function SubAccountPicker({ platform, connection, onUpdate }) {
  const [busy, setBusy] = useState(false);

  const conn = connection || {};
  const subs = conn.sub_accounts || [];
  const status = conn.discovery_status;
  const isRunning = status === 'running';
  const currentSubId = platform === 'volumetrica'
    ? (conn.account_number || null)
    : (conn.selected_sub_account_id || null);

  const handleDiscover = async () => {
    setBusy(true);
    try {
      const fn = platform === 'tradovate'
        ? api.discoverTradovateSubAccounts
        : api.discoverVolumetricaSubAccounts;
      await fn(conn.connection_id);
      toast.info('Discovering sub-accounts — this can take 30–60 seconds');
      // Poll every 4s up to 90s
      let elapsed = 0;
      const poll = setInterval(async () => {
        elapsed += 4;
        await onUpdate?.();
        if (elapsed >= 90) clearInterval(poll);
      }, 4000);
      // Cleanup safety
      setTimeout(() => clearInterval(poll), 100_000);
    } catch (e) {
      toast.error(e.message || 'Failed to start discovery');
    }
    setBusy(false);
  };

  const handlePick = async (sub) => {
    setBusy(true);
    try {
      const fn = platform === 'tradovate'
        ? api.selectTradovateSubAccount
        : api.selectVolumetricaSubAccount;
      await fn(conn.connection_id, { sub_account_id: sub.id, sub_account_label: sub.label || sub.name });
      toast.success(`Now trading on ${sub.label || sub.name}`);
      await onUpdate?.();
    } catch (e) {
      toast.error(e.message || 'Failed to set sub-account');
    }
    setBusy(false);
  };

  // No discovery yet → show "Discover" CTA
  if (!subs.length && !isRunning) {
    return (
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
        <button
          data-testid={`discover-${platform}-${conn.connection_id}`}
          onClick={handleDiscover}
          disabled={busy}
          style={{
            width: '100%', height: 30, background: 'transparent',
            border: '1px solid rgba(41,121,255,0.35)', color: 'var(--pp-accent)',
            fontSize: 10, fontWeight: 700, cursor: busy ? 'wait' : 'pointer',
            letterSpacing: '0.1em',
          }}
        >
          {busy ? 'STARTING…' : 'DISCOVER SUB-ACCOUNTS'}
        </button>
        {status === 'failed' && conn.discovery_error && (
          <p style={{ margin: '6px 0 0', fontSize: 10, color: 'var(--pp-warning)' }}>
            Last attempt: {conn.discovery_error}
          </p>
        )}
      </div>
    );
  }

  if (isRunning) {
    return (
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>
          🔍 Retrieving account list… (~30–60s)
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(255,255,255,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', letterSpacing: '0.1em', fontWeight: 700 }}>
          SUB-ACCOUNTS ({subs.length})
        </p>
        <button
          data-testid={`rediscover-${platform}-${conn.connection_id}`}
          onClick={handleDiscover}
          disabled={busy}
          style={{
            background: 'none', border: 'none', color: 'var(--pp-accent)',
            fontSize: 10, cursor: busy ? 'wait' : 'pointer', fontWeight: 600,
          }}
        >↻ refresh</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {subs.map((sub) => {
          const selected = sub.id === currentSubId || sub.name === currentSubId;
          return (
            <button
              key={sub.id || sub.name}
              data-testid={`pick-${platform}-${sub.id || sub.name}`}
              onClick={() => !selected && handlePick(sub)}
              disabled={busy || selected}
              style={{
                background: selected ? 'rgba(0,255,148,0.10)' : 'var(--pp-overlay)',
                border: selected ? '1px solid var(--pp-success)' : '1px solid var(--pp-border)',
                color: selected ? 'var(--pp-success)' : 'var(--pp-text)',
                padding: '8px 10px', fontSize: 12, cursor: selected ? 'default' : 'pointer',
                textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {sub.label || sub.name}{sub.status ? ` · ${sub.status}` : ''}
              </span>
              {selected && <span style={{ fontSize: 10, fontWeight: 700 }}>✓ ACTIVE</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
